import React, { useEffect, useState } from 'react';
import { Grid, Card, CardContent, Typography, Box, Alert, Skeleton, FormControlLabel, Switch, Tooltip, Button, IconButton, Chip, Tabs, Tab } from '@mui/material';
import { Check, Info as InfoIcon, Add as AddIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { AIScoring, MLData, SeatRecommendationComponentProps } from '../utils/SingleEventAnalysisTab.types';
import SeatFactorModal, { useSeatFactorModal } from '../SeatFactorModal';
import SeatActionDialog from '../../SeatActionDialog';
import { useNotify, useDataProvider } from 'react-admin';
import { getRecommendationIcon, getScoreColor } from '../../../AnalysisPage';
import { SeatRecommendation, AIPurchasedTicket, TopOpportunity } from '../../../models/seat';

const REAL_TICKET_API_URL = process.env.REAL_TICKET_API_URL || 'https://localhost:5002';

/**
 * Returns the color for the action status Chip based on purchase status
 * @param action - The action/purchase status string
 * @returns MUI Chip color prop value
 */
export const getActionChipColor = (action: string): 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary' | 'secondary' => {
    if (!action) return 'default';
    
    const lowerAction = action.toLowerCase();
    if (lowerAction.includes('buy')) return 'primary';
    if (lowerAction === 'sold') return 'success';
    if (lowerAction === 'partiallysold') return 'success';
    if (lowerAction === 'partiallyexpired') return 'warning';
    if (lowerAction === 'expired') return 'error';
    if (lowerAction === 'new') return 'info';
    if (lowerAction === 'ignore') return 'error';
    return 'default';
};

/**
 * SeatRecommendationComponent
 *
 * Component that displays seat recommendations with filtering capabilities.
 * Handles seat action dialogs and seat factor analysis modals.
 *
 * Features:
 * - Loading state with skeleton UI
 * - Seat recommendation cards with scores and pricing
 * - Toggle to show/hide filtered results
 * - Seat action management (add/edit actions for seats)
 * - Integration with seat factor analysis modal
 * - Price filtering based on pre-filtering rules
 *
 * @param props - Component props containing recommendations and display functions
 * @returns Seat recommendations section with cards
 */
const SeatRecommendationComponent: React.FC<SeatRecommendationComponentProps> = ({
    analysisCompleted,
    seatRecommendations,
    setSeatRecommendations,
    isSeatRecommendationsLoading,
    preFilteringRules,
    aiScoring,
    setAiScoring,
    selectedEvent,
    setSearchResults
}) => {
    const [showAll, setShowAll] = useState(false);
    const [seatActionDialogOpen, setSeatActionDialogOpen] = useState(false);
    const [purchaseSeatData, setPurchaseSeatData] = useState<AIPurchasedTicket | null>(null);
    const [currentTab, setCurrentTab] = useState(0); // 0 for Manual, 1 for AI Generated
    const [mlTopLots, setMlTopLots] = useState<TopOpportunity[]>([]);
    const [mlTopLotsFetched, setMlTopLotsFetched] = useState(false);
    const [isLoadingMlTopLots, setIsLoadingMlTopLots] = useState(false);
    const [isWaitingEmpty, setIsWaitingEmpty] = useState(false);

    const { modalState: seatFactorModalState, openModal: openSeatFactorModal, closeModal: closeSeatFactorModal } = useSeatFactorModal();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    useEffect(() => {
        // If analysis is completed and seatRecommendations is empty, start waiting state
        if (analysisCompleted && seatRecommendations?.length === 0 && (aiScoring?.overallEventScore ?? 0) > 60) {
            setIsWaitingEmpty(true);
        
            const timer = setTimeout(() => {
                setIsWaitingEmpty(false);
            }, 5000); // wait for 5 seconds before resetting

            return () => clearTimeout(timer); // Cleanup if component unmount or data changes before timeout => no setState exist => error when trying to set state on unmounted component
        } else {
            // If data returns or is loading, reset this state
            setIsWaitingEmpty(false);
        }
    }, [analysisCompleted, seatRecommendations]);

    /**
     * Event Date Validation Logic:
     * Checks if the event date is in the future (greater than current date).
     * Manual seat purchase actions should only be allowed for future events.
     * @returns true if event is in the future, false otherwise
     */
    const isEventInFuture = (): boolean => {
        const eventDate = selectedEvent?.dateUtc;
        if (!eventDate) {
            return false; // If no event date, assume not in future
        }
        const eventDateTime = new Date(eventDate);
        const currentDateTime = new Date();
        return eventDateTime > currentDateTime;
    };

    /**
     * Handles the show all toggle switch
     */
    const handleShowAllToggle = () => {
        setShowAll(prev => !prev);
    };
    /**
 * Fetches ML summary from the API including topLots, recommendationCounts, and eventQualityScore.
 * This function is exported for use in ScoringSection to pre-fetch ML data.
 *
 * @param eventId - The event ID to fetch ML summary for
 * @returns Promise<{topLots: TopOpportunity[], recommendationCounts?: object, eventQualityScore?: number} | null>
 * Returns an object with topLots array and summary details, or null if error occurs
 */
    const fetchMLSummary = async (eventId: bigint) => {
        setIsLoadingMlTopLots(true);
        const MAX_RETRIES = 2;
        const RETRY_DELAY_MS = 4000; // 4 seconds

        try {
            if (!eventId) {
                console.warn('Event ID not available for ML summary');
                return null;
            }

            // Retry loop: attempt initial fetch + up to MAX_RETRIES additional attempts
            for (let retryCount = 0; retryCount <= MAX_RETRIES; retryCount++) {
                const response = await fetch(`${REAL_TICKET_API_URL}/events/${eventId}/ml-summary`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    if (response.status === 404 && retryCount < MAX_RETRIES) {
                        console.log(`Server returns 404 for event ID: ${eventId}. Message: ${response.statusText}. Retrying...`);
                        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
                        continue;
                    }
                    else if (response.status !== 404) throw new Error(`Failed to fetch ML summary: ${response.status}`);
                    else break; // Exit loop on final 404, prevent below code run
                }

                const data: MLData = await response.json();
            
                // Extract topLots from the response
                const topLots = data.topLots || [];
                // Data received (empty or not) and no more retries needed, return mldata
                setMlTopLots(topLots);
                setMlTopLotsFetched(true);
                const updatedScoring = { ...aiScoring, mlData: data } as AIScoring;
                setAiScoring(updatedScoring);
                // Also update search results with mlData
                setSearchResults((prev: any) => {
                    // Guard clause for empty previous results: in case prev is null/undefined/empty object when call this function from purchasedSeat tab
                    if (!prev || Object.keys(prev).length === 0 || !prev.data) return prev;

                    const updatedResults = prev.data.map((item: any) => {
                        if (item.siteId === selectedEvent?.siteId) {
                            return { ...item,
                                mL_Score: data.eventQualityScore || null,
                                mL_BuyCount: data.summaryDetails.recommendationCounts?.buy || 0,
                                mL_StrongBuyCount: data.summaryDetails.recommendationCounts?.strongBuy || 0 };
                        }
                        return item;
                    });
                    return { ...prev, data: updatedResults };
                });
                break;
            }
        
            // If we exhaust all retries, return empty result
            return null;
        } catch (error) {
            console.error('Error fetching ML opportunities:', error);
            notify(`Failed to fetch ML opportunities: ${error instanceof Error ? error.message : 'Unknown error'}`, { type: 'error' });
            setMlTopLots([]);
        } finally {
            setIsLoadingMlTopLots(false);
        }
    };
    /**
     * Handles tab change and uses pre-fetched ML topLots from aiScoring
     */
    const handleTabChange = async (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
                
        // Fetch ML opportunities only on first switch to AI Generated tab or aiScoring.mlData is null
        let eventId = aiScoring?.eventId;
        if (newValue === 1 && !mlTopLotsFetched && eventId) {
            if (!aiScoring?.mlData?.topLots) await fetchMLSummary(eventId);
            else setMlTopLots(aiScoring.mlData.topLots || []); // Use existing mlData topLots
        }
    };

    /**
     * Handles manual retry button click
     * Manually triggers fetchMLSummary when user clicks the retry button
     */
    const handleManualRetry = async () => {
        let eventId = aiScoring?.eventId;
        if (eventId) {
            await fetchMLSummary(eventId);
        } else {
            notify('Event ID not available for ML opportunities', { type: 'warning' });
        }
    };

    /**
     * Opens the seat action dialog for a specific seat
     * Event Date Validation: Only allows opening the dialog for future events
     * @param seat - The seat object from the seatRecommendations array, or null for manual entry
     * @param mlData - Optional ML opportunity data for AI Generated tab
     */
    const handleOpenSeatActionDialog = (seat: SeatRecommendation | null, mlData?: TopOpportunity) => {
        // Validate event date - only allow for future events
        if (!isEventInFuture()) {
            notify('Manual seat purchase actions are only allowed for future events', { type: 'warning' });
            return;
        }

        let eventName: string =  aiScoring?.eventName || '';
        let siteId: string = aiScoring?.siteId || '';
        let eventDate: string = aiScoring?.prepareData?.eventDate || selectedEvent?.dateUtc || '';
        let venue: string = selectedEvent?.venue || '';
        let marketType: string = selectedEvent?.type || '';
        let deliveryMethod: number = aiScoring?.prepareData?.deliveryMethod || selectedEvent?.deliveryMethod || 0;
        
        // Handle ML opportunity data
        if (mlData) {
            let eventId = BigInt(aiScoring?.eventId  || aiScoring?.prepareData?.eventId || 0);
            let analysisId = BigInt(aiScoring?.analysisId ?? 0);
            
            const mlSeatData = AIPurchasedTicket.fromMLData(mlData, eventId, analysisId);
            mlSeatData.eventName = eventName;
            mlSeatData.siteId = siteId;
            mlSeatData.eventDate = eventDate;
            mlSeatData.venue = venue;
            mlSeatData.marketType = marketType;
            mlSeatData.deliveryMethod = deliveryMethod;
            setPurchaseSeatData(mlSeatData);
        }
        // Pre-populate dialog with seat data from recommendations if not already saved
        // For manual entry (seat === null), start with empty data
        else if (seat) {
            const initialSeatData = AIPurchasedTicket.fromSeatRecommendation(seat);
            initialSeatData.eventName = eventName;
            initialSeatData.siteId = siteId;
            initialSeatData.eventDate = eventDate;
            initialSeatData.venue = venue;
            initialSeatData.marketType = marketType;
            initialSeatData.deliveryMethod = deliveryMethod;
            setPurchaseSeatData(initialSeatData);
        } else {
            let eventId = BigInt(aiScoring?.eventId  || aiScoring?.prepareData?.eventId || 0);
            // get analysis id from aiScoring
            let analysisId = BigInt(aiScoring?.analysisId ?? 0);
            const emptyData = AIPurchasedTicket.createEmpty(eventId, analysisId);
            emptyData.eventName = eventName;
            emptyData.siteId = siteId;
            emptyData.eventDate = eventDate;
            emptyData.venue = venue;
            emptyData.marketType = marketType;
            emptyData.deliveryMethod = deliveryMethod;
            setPurchaseSeatData(emptyData);
        }
        
        setSeatActionDialogOpen(true);
    };

    /**
     * Closes the seat action dialog
     */
    const handleCloseSeatActionDialog = () => {
        setSeatActionDialogOpen(false);
    };

    /**
     * Handles saving seat action data
     * @param seatData - The seat data to save
     */
    const handleSaveSeatAction = async (seatData: AIPurchasedTicket | null) => {
        // Validate that at least the purchase details are filled
        if (!seatData?.purchasedSectionName || !seatData?.purchasedRowName || !seatData?.purchasedSeatRange) {
            notify('Please fill in purchase details (Section, Row, and Seat Range are required).', { type: 'error' });
            return;
        }
        
        // Get current user identity for purchasedBy_UserId field
        const user = await dataProvider.currentUser();
        try {
            // Map frontend data to backend API format
            // Convert BigInt types to number to avoid JSON serialization errors
            const apiPayload = {
                seatAnalysisResultId: seatData.seatAnalysisResultId ? Number(seatData.seatAnalysisResultId) : null, // Allow null for manual entry without analysis
                singleAnalysisId: seatData.singleAnalysisId ? Number(seatData.singleAnalysisId) : null,
                originalEventId: seatData.originalEventId ? Number(seatData.originalEventId) : 0, // Allow 0 for manual entry
                originalSeatId: seatData.originalSeatId || '', // Allow empty for manual entry
                originalSectionName: seatData.originalSectionName || null,
                originalRowName: seatData.originalRowName || null,
                originalSeatRange: seatData.originalSeatRange || null,
                originalQuantity: seatData.originalQuantity || null,
                originalPricePerTicket: seatData.originalPricePerTicket || null,
                originalAIScore: seatData.originalAIScore || null,
                purchasedSectionName: seatData.purchasedSectionName,
                purchasedRowName: seatData.purchasedRowName,
                purchasedSeatRange: seatData.purchasedSeatRange,
                purchasedQuantity: seatData.purchasedQuantity,
                actualPricePerTicket: seatData.actualPricePerTicket,
                purchaseStatus: seatData.purchaseStatus,
                purchasedBy_UserId: user.email || 'unknown',
                // New purchase-related fields
                ticketLink: seatData.ticketLink,
                publicNote: seatData.publicNote,
                internalNote: seatData.internalNote,
                sellingPrice: seatData.sellingPrice,
                orderNumber: seatData.orderNumber,
                account: seatData.account,
                deliveryMethod: seatData.deliveryMethod,
                priceLevel: seatData.priceLevel,
                notes: seatData.notes,
                lotAttributes: seatData.lotAttributes,
                supervisorNotes: seatData.supervisorNotes,
                trackingVersion: seatData.trackingVersion,
                highRisk: seatData.highRisk
            };

            // Call the save-seat-action API endpoint
            const response = await fetch(`${REAL_TICKET_API_URL}/save-seat-action`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(apiPayload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save seat action');
            }

            const result = await response.json();
            
            notify(
                seatData.originalSeatId
                    ? `Purchase Seat updated (ID: ${result.recordId})`
                    : `Manual purchase entry saved successfully (ID: ${result.recordId})`,
                { type: 'success' }
            );
            if (seatData.originalSeatId) {
                // Update local seatRecommendations state to reflect the new/updated purchaseStatus
                setSeatRecommendations(seatRecommendations.map(item => {
                    if (item.seatId === seatData.originalSeatId) {
                        return { ...item, purchaseStatus: seatData.purchaseStatus };
                    }
                    return item;
                }));
            } else {
                // Update local ML opportunities state to reflect the new/updated purchaseStatus
                setMlTopLots(mlTopLots.map(item => {
                    if (seatData.lotAttributes?.includes(item.lotId)) {
                        return { ...item, purchaseStatus: seatData.purchaseStatus };
                    }
                    return item;
                }));
            }
        } catch (error) {
            console.error('Error saving seat action:', error);
            notify(
                `Failed to save seat action: ${error instanceof Error ? error.message : 'Unknown error'}`,
                { type: 'error' }
            );
        }
    };

    // create a list variable to filter prefilteringRules value in list
    const applyFilterList = ['maxPrimaryPrice'];
    const maxPrimarySeatPriceRule = preFilteringRules?.find((rule: any) => rule.variable === 'maxPrimaryPrice');

    /**
     * Converts ML opportunity to a format compatible with SeatFactorModal
     * Maps buyReasons (string array) to positiveFactors and riskFactors
     */
    const convertMLOpportunityToSeatData = (opportunity: TopOpportunity): SeatRecommendation => {
        return {
            eventId: BigInt(aiScoring?.eventId || aiScoring?.prepareData?.event?.id || 0),
            seatId: opportunity.lotId || `ml-${opportunity.section}-${opportunity.row}`,
            section: opportunity.section,
            row: opportunity.row,
            seatRange: opportunity.seats || opportunity.seatRange,
            quantity: opportunity.targetQuantity || opportunity.quantity,
            price: opportunity.price,
            factors: {
                positiveFactors: opportunity.buyReasons || [],
                riskFactors: opportunity.riskFactors || []
            },
            lotId: opportunity.lotId,
        } as SeatRecommendation;
    };

    const renderSkeleton = () => (
        <>
            <Skeleton variant="text" width="60%" height={32} sx={{ mb: 2 }} />
            {[1, 2, 3].map((idx) => (
                <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Skeleton variant="text" width="40%" height={20} />
                            <Skeleton variant="text" width="30%" height={20} />
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </>
    );
    /**
     * Renders ML opportunity cards
     */
    const renderMLOpportunities = () => {
        if (isLoadingMlTopLots) {
            return renderSkeleton();
        }

        if (mlTopLots.length === 0) {
            return (
                <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                    No ML opportunities available for this event.
                </Alert>
            );
        }

        return mlTopLots.map((opportunity, idx) => (
            <Card key={idx} variant="outlined" sx={{ mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                <CardContent sx={{ p: 2, flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                                {opportunity.section} - Row {opportunity.row}
                            </Typography>
                            {opportunity.purchaseStatus && (
                                <Chip
                                    label={opportunity.purchaseStatus}
                                    size="small"
                                    color={getActionChipColor(opportunity.purchaseStatus)}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                />
                            )}
                        </Box>
                        <IconButton
                            size="large"
                            onClick={() => openSeatFactorModal(convertMLOpportunityToSeatData(opportunity))}
                            sx={{ ml: 1, p: 0.25 }}
                            title="View Factors"
                            aria-label="View ML opportunity factors"
                        >
                            <InfoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                        Seats: {opportunity.seats || opportunity.seatRange || 'N/A'} | Quantity: {opportunity.targetQuantity || opportunity.quantity || 'N/A'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="body2">
                            Score: {opportunity.qualityScore ? opportunity.qualityScore.toFixed(1) : 'N/A'}
                            {getRecommendationIcon(opportunity.recommendation ? opportunity.recommendation : '', { sx : { fontSize: 18 } })}
                            {opportunity.recommendation && (
                                <Typography color="primary" variant='body2' component='span'>{` (${opportunity.recommendation})`}</Typography>
                            )}
                        </Typography>
                        <Typography variant="body2" color="success.main">
                            ${opportunity.price ? opportunity.price.toFixed(1) : '-'}
                        </Typography>
                    </Box>
                    <Typography variant="body2">
                        Predicted ROI: <span style={{ color: '#0D9488' }}>{opportunity.predictedROI !== null && opportunity.predictedROI !== undefined ? (opportunity.predictedROI * 100).toFixed(1) + '%' : 'N/A'}</span>
                    </Typography>
                    <Typography variant="body2">
                        Predicted Days To Sell: <span style={{ color: '#0D9488' }}>{opportunity.predictedDaysToSell ?? 'N/A'}</span>
                    </Typography>
                </CardContent>
                {/* Buy button for ML opportunities */}
                {isEventInFuture() && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        borderLeft: '1px solid rgba(0, 0, 0, 0.12)'
                    }}>
                        <Button
                            onClick={() => handleOpenSeatActionDialog(null, opportunity)}
                            sx={{
                                minWidth: 'auto',
                                px: 1.5,
                                height: '100%',
                                borderRadius: 0,
                                '&:hover': {
                                    backgroundColor: 'action.hover'
                                },
                                background: '#4040ab2e'
                            }}
                            title={'Buy Opportunity'}
                            aria-label={'Buy opportunity'}
                        >
                            <AddIcon fontSize="small" />
                        </Button>
                    </Box>
                )}
            </Card>
        ));
    };

    /**
     * Renders the main content of the seat recommendations section
     */
    const renderContent = () => {
        // --- Case 1: Loading state ---
        if (isSeatRecommendationsLoading) {
            return renderSkeleton();
        }

        // --- Case 2: If analysis not completed, render nothing ---
        if (!analysisCompleted) {
            return <></>;
        }

        // --- Case 3: Analysis completed but no recommendations ---
        if (seatRecommendations?.length === 0) {
            if (isWaitingEmpty) return renderSkeleton();

            return (
                <Alert icon={<Check fontSize="inherit" />} severity="warning">
                    No seat recommendations available for this event.
                </Alert>
            );
        }

        // --- Case 4: Analysis completed with recommendations ---
        if (seatRecommendations?.length > 0) {
            let finalRecommendations = seatRecommendations;
            if (!showAll) {  // If "Show All" is off, apply max price filter if exists
                finalRecommendations = maxPrimarySeatPriceRule?.value ? seatRecommendations.filter(seat => seat.price ? seat.price <= maxPrimarySeatPriceRule.value : false) : seatRecommendations;
            }
            if (finalRecommendations.length === 0) {
                return (
                    <Alert icon={<InfoIcon fontSize="inherit" />} severity="info">
                        No seat recommendations match the applied filters.
                    </Alert>
                );
            }
            return (
                finalRecommendations.map((seat, idx) => (
                    <Card key={idx} variant="outlined" sx={{ mb: 2, display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
                        <CardContent sx={{ p: 2, flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="subtitle2">
                                        {seat.section} - Row {seat.row}
                                    </Typography>
                                    {seat.purchaseStatus && (
                                        <Chip
                                            label={seat.purchaseStatus}
                                            size="small"
                                            color={getActionChipColor(seat.purchaseStatus)}
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                    )}
                                </Box>
                                <IconButton
                                    size="large"
                                    onClick={() => openSeatFactorModal(seat)}
                                    sx={{ ml: 1, p: 0.25 }}
                                    title="View Factors"
                                    aria-label="View seat factor analysis"
                                >
                                    <InfoIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Box>
                            <Typography variant="caption" color="textSecondary">
                                Seats: {seat.seatRange} | Quantity: {seat.quantity}
                            </Typography>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                <Typography variant="body2">
                                    {/* Display "Symbolic" for migrated records, otherwise show actual score */}
                                    Score: {seat.isMigrated ? 'Symbolic' : seat.scores?.overallScore.toFixed(1)} {getRecommendationIcon(seat.recommendation ? seat.recommendation.decision : '', { sx : { fontSize: 18 } })}
                                    <Typography color={getScoreColor(seat.scores ? seat.scores.overallScore : 0)} variant='body2' component='span'>{` (${seat.recommendation?.decision})`}</Typography>
                                </Typography>
                                <Typography variant="body2" color="success.main">
                                    ${seat.price ? seat.price.toFixed(1) : '-'}
                                </Typography>
                            </Box>
                        </CardContent>
                        {/* Purchase Seat button only displays when event date is in the future */}
                        {isEventInFuture() && (
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'stretch',
                                borderLeft: '1px solid rgba(0, 0, 0, 0.12)'
                            }}>
                                <Button
                                    onClick={() => handleOpenSeatActionDialog(seat)}
                                    sx={{
                                        minWidth: 'auto',
                                        px: 1.5,
                                        height: '100%',
                                        borderRadius: 0,
                                        '&:hover': {
                                            backgroundColor: 'action.hover'
                                        },
                                        background: '#4040ab2e'
                                    }}
                                    title={'Purchase Seat'}
                                    aria-label={'Purchase seat'}
                                >
                                    <AddIcon fontSize="small" />
                                </Button>
                            </Box>
                        )}
                    </Card>
                ))
            );
        }

        return null;
    };

    // Only render the entire card if loading or analysis is completed
    if (!isSeatRecommendationsLoading && !analysisCompleted) {
        return null;
    }

    /**
     * Generates tooltip title for the filter toggle
     */
    const generateTooltipTitle = () => {
        const filterString = preFilteringRules?.filter((rule: any) => applyFilterList.includes(rule.variable)).map((rule: any) => {
            return `${rule.variable}-${rule.value.toString()}`;
        }).join(', ') || 'No filters applied';
        return `Apply filter: \n ${filterString}`;
    };

    return (
        <>
            <Grid size={3}>
                <Card>
                    <CardContent>
                        {/* Header with Tabs */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, marginBottom: '5px' }}>
                            <Typography variant="h6" component="div">
                                Seat Recommendations
                            </Typography>
                            {/* Switch displayed only when analysis is completed and there are results and on Manual tab */}
                            {currentTab === 0 && analysisCompleted && seatRecommendations?.length > 0 && (
                                <FormControlLabel
                                    control={<Switch checked={showAll} onChange={handleShowAllToggle} />}
                                    label={showAll ? 'Show All' : <Tooltip title={generateTooltipTitle()}>
                                        <span>Hide</span>
                                    </Tooltip>}
                                    sx={{ mr: 0 }} // Decrease right margin if needed
                                />
                            )}
                        </Box>

                        {/* Tabs */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Tabs
                                value={currentTab}
                                onChange={handleTabChange}
                                sx={{ flexGrow: 1, minHeight: 'auto' }}
                                variant="fullWidth"
                            >
                                <Tab label="Manual" sx={{ minHeight: 'auto', py: 1 }} />
                                <Tab sx={{ minHeight: 'auto', py: 1 }}
                                    label={ <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>ML TopLots</Box>}
                                />
                            </Tabs>
                            {/* Retry button - only show when ML TopLots tab is active */}
                            {currentTab === 1 && (
                                <IconButton
                                    size="small"
                                    onClick={handleManualRetry}
                                    disabled={isLoadingMlTopLots}
                                    title="Retry fetching ML opportunities"
                                    aria-label="Retry ML opportunities"
                                    sx={{ ml: 1 }}
                                >
                                    <RefreshIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>

                        {/* Tab Content */}
                        {currentTab === 0 ? (
                            <>
                                {/* "Add Manual Purchase Seat" button only displays when event date is in the future */}
                                {isEventInFuture() && (
                                    <Button variant="outlined" startIcon={<AddIcon />} size='small' sx={{ mb: 2 }} fullWidth
                                        onClick={() => {
                                            // Open dialog with empty/manual entry mode
                                            handleOpenSeatActionDialog(null);
                                        }}
                                    >
                                            Add Manual Purchase Seat
                                    </Button>
                                )}
                                {/* Call function to render dynamic content for Manual tab */}
                                {renderContent()}
                            </>
                        ) : (
                            <>
                                {/* AI Generated tab content */}
                                {renderMLOpportunities()}
                            </>
                        )}
                    </CardContent>
                </Card>
            </Grid>

            {/* Seat Action Dialog */}
            <SeatActionDialog
                open={seatActionDialogOpen}
                onClose={handleCloseSeatActionDialog}
                onSave={handleSaveSeatAction}
                initialData={purchaseSeatData}
            />

            {/* Seat Factor Modal */}
            <SeatFactorModal modalState={seatFactorModalState} onClose={closeSeatFactorModal} />
        </>
    );
};

export default SeatRecommendationComponent;
